import { useCallback, useEffect, useRef, useState } from "react";
import { AppShell } from "../components/AppShell";
import { api, apiError } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { useSpeech } from "../hooks/useSpeech";
import { toast } from "sonner";
import {
  Camera, Monitor, Play, Square, Volume2, VolumeX, SwitchCamera,
  Stethoscope, Eye, Compass, Loader2, Send, Radio, Mic, MicOff,
  Utensils, ScanSearch, Flame,
} from "lucide-react";

const MODES = [
  { id: "diagnostic", label: "Diagnostic", icon: Stethoscope, hint: "Repère pannes & problèmes techniques" },
  { id: "describe", label: "Décrire", icon: Eye, hint: "Décrit l'environnement en direct" },
  { id: "guide", label: "Guidage", icon: Compass, hint: "Te guide étape par étape" },
  { id: "food", label: "Alimentaire", icon: Utensils, hint: "Nomme les aliments, estime calories & macros" },
  { id: "objects", label: "Objets", icon: ScanSearch, hint: "Pointe et nomme les objets visibles" },
];

const DETECT_MODES = new Set(["food", "objects"]);

const INTERVAL_MS = 2200;

const BOX_PALETTE = [
  "#22d3ee", "#34d399", "#f59e0b", "#f472b6", "#a78bfa", "#60a5fa", "#fb7185", "#4ade80",
];

function DetectionOverlay({ detections, mode }) {
  const items = detections?.items || [];
  if (!DETECT_MODES.has(mode) || detections?.mode !== mode || items.length === 0) return null;
  return (
    <div data-testid="vision-overlay" className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      {items.map((it, i) => {
        // box_2d is clamped server-side to [0,1000] → always inside the 16:9 stage
        const [ymin, xmin, ymax, xmax] = it.box_2d;
        const left = xmin / 10;
        const top = ymin / 10;
        const width = (xmax - xmin) / 10;
        const height = (ymax - ymin) / 10;
        const color = BOX_PALETTE[i % BOX_PALETTE.length];
        const cx = (xmin + xmax) / 2;
        // keep the label inside the frame: above the box when there is room,
        // otherwise anchored just inside its top edge; hug the right edge on the right half.
        const roomAbove = ymin > 220;
        const anchorRight = cx > 520;
        const kcal = typeof it.calories === "number" ? it.calories : null;
        const caret = (down) => (
          <span
            className={`block h-0 w-0 ${anchorRight ? "mr-3 self-end" : "ml-3 self-start"}`}
            style={{
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              ...(down ? { borderTop: `6px solid ${color}` } : { borderBottom: `6px solid ${color}` }),
            }}
          />
        );
        return (
          <div
            key={`${it.label}-${i}`}
            data-testid={`vision-box-${i}`}
            className="absolute"
            style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}
          >
            {/* bounding box */}
            <div
              className="absolute inset-0 rounded-md"
              style={{ border: `2px solid ${color}`, boxShadow: `0 0 10px ${color}66, inset 0 0 12px ${color}22` }}
            />
            {/* label tag with pointer arrow — clamps to the box edge nearest the center */}
            <div
              className={`absolute flex flex-col ${anchorRight ? "right-0 items-end" : "left-0 items-start"}`}
              style={roomAbove ? { bottom: "100%", marginBottom: 6 } : { top: 2 }}
            >
              <div
                className="max-w-[52vw] break-words rounded-md px-2 py-1 font-mono-hud text-[11px] leading-tight text-black shadow-lg sm:max-w-[240px]"
                style={{ background: color }}
              >
                <span className="font-bold tracking-wide">{it.label}</span>
                {kcal != null && <span className="ml-1.5 font-semibold">≈ {kcal} kcal</span>}
                {mode === "food" && (
                  <span className="mt-0.5 block text-[9.5px] font-medium opacity-80">
                    {typeof it.portion_g === "number" ? `${it.portion_g} g` : ""}
                    {(typeof it.protein_g === "number" || typeof it.carbs_g === "number" || typeof it.fat_g === "number")
                      ? `${typeof it.portion_g === "number" ? " · " : ""}P${it.protein_g ?? "–"} G${it.carbs_g ?? "–"} L${it.fat_g ?? "–"}`
                      : ""}
                  </span>
                )}
              </div>
              {roomAbove && caret(true)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Vision() {
  const { user } = useAuth();
  const { speak, stopSpeaking, unlockAudio, speaking, startWhisper, stopWhisper, whisperRecording } = useSpeech();

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const [source, setSource] = useState("camera");
  const [facing, setFacing] = useState("environment");
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("diagnostic");
  const [speakOn, setSpeakOn] = useState(true);
  const [analyses, setAnalyses] = useState([]);
  const [latest, setLatest] = useState("");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamOn, setStreamOn] = useState(false);
  const [detections, setDetections] = useState({ items: [], total: null, mode: null });

  // refs mirrored for the async loop
  const runningRef = useRef(false);
  const modeRef = useRef(mode);
  const speakRef = useRef(speakOn);
  const speakingRef = useRef(false);
  const questionRef = useRef(null);
  const histRef = useRef([]);
  useEffect(() => { modeRef.current = mode; setDetections({ items: [], total: null, mode: null }); }, [mode]);
  useEffect(() => { speakRef.current = speakOn; }, [speakOn]);
  useEffect(() => { speakingRef.current = speaking; }, [speaking]);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreamOn(false);
  }, []);

  const startStream = useCallback(async (src, face) => {
    stopStream();
    try {
      let stream;
      if (src === "screen") {
        stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false });
        stream.getVideoTracks()[0].addEventListener("ended", () => {
          runningRef.current = false;
          setRunning(false);
          stopStream();
        });
      } else {
        // Keep constraints minimal — forcing high res / aspectRatio makes several
        // Android cameras return a black frame. `ideal` facingMode avoids a hard
        // constraint failure when the device has no exact match.
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: face } },
            audio: false,
          });
        } catch (_) {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
      }
      streamRef.current = stream;
      if (videoRef.current) {
        const v = videoRef.current;
        v.srcObject = stream;
        v.muted = true;
        v.setAttribute("playsinline", "true");
        try {
          await v.play();
        } catch (_) {
          // Android often needs a second attempt once metadata is ready.
          v.onloadedmetadata = () => v.play().catch(() => {});
        }
      }
      setStreamOn(true);
      return true;
    } catch (e) {
      toast.error(
        src === "screen"
          ? "Partage d'écran refusé ou indisponible"
          : "Caméra indisponible — autorisez l'accès dans le navigateur"
      );
      return false;
    }
  }, [stopStream]);

  const captureFrame = useCallback(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth) return null;
    // Center-crop the source to 16:9 so the analysed frame matches what is displayed
    const TARGET = 16 / 9;
    let sw = v.videoWidth;
    let sh = v.videoHeight;
    if (sw / sh > TARGET) sw = Math.round(sh * TARGET);
    else sh = Math.round(sw / TARGET);
    const sx = Math.round((v.videoWidth - sw) / 2);
    const sy = Math.round((v.videoHeight - sh) / 2);
    c.width = Math.min(768, sw);
    c.height = Math.round(c.width / TARGET);
    const ctx = c.getContext("2d");
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.6);
  }, []);

  const tick = useCallback(async () => {
    if (!runningRef.current) return;
    const frame = captureFrame();
    if (frame) {
      setBusy(true);
      try {
        if (DETECT_MODES.has(modeRef.current)) {
          const { data } = await api.post("/vision/detect", {
            image: frame,
            mode: modeRef.current,
            question: questionRef.current,
          });
          questionRef.current = null;
          const items = Array.isArray(data.items) ? data.items : [];
          setDetections({ items, total: data.total_calories ?? null, mode: modeRef.current });
          const text = (data.summary || "").trim();
          if (text) {
            setLatest(text);
            setAnalyses((prev) => [{ id: `${Date.now()}`, text, at: Date.now() }, ...prev].slice(0, 25));
            if (speakRef.current && !speakingRef.current) {
              unlockAudio();
              speak(text, user?.voice);
            }
          }
        } else {
          const { data } = await api.post("/vision/analyze", {
            image: frame,
            mode: modeRef.current,
            question: questionRef.current,
            history: histRef.current,
          });
          questionRef.current = null;
          const text = (data.analysis || "").trim();
          if (text) {
            histRef.current = [...histRef.current, text].slice(-4);
            setLatest(text);
            setAnalyses((prev) => [{ id: `${Date.now()}`, text, at: Date.now() }, ...prev].slice(0, 25));
            if (speakRef.current && !speakingRef.current) {
              unlockAudio();
              speak(text, user?.voice);
            }
          }
        }
      } catch (e) {
        // keep the loop alive; surface once
        setLatest("(analyse interrompue, nouvelle tentative…)");
      } finally {
        setBusy(false);
      }
    }
    if (runningRef.current) timerRef.current = setTimeout(tick, INTERVAL_MS);
  }, [captureFrame, speak, unlockAudio, user]);

  const start = useCallback(async () => {
    unlockAudio();
    const ok = streamOn || (await startStream(source, facing));
    if (!ok) return;
    histRef.current = [];
    runningRef.current = true;
    setRunning(true);
    timerRef.current = setTimeout(tick, 600);
  }, [streamOn, startStream, source, facing, tick, unlockAudio]);

  const stop = useCallback(() => {
    runningRef.current = false;
    setRunning(false);
    clearTimeout(timerRef.current);
    setDetections({ items: [], total: null, mode: null });
    stopSpeaking();
  }, [stopSpeaking]);

  const flipCamera = useCallback(async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    if (source === "camera" && streamOn) await startStream("camera", next);
  }, [facing, source, streamOn, startStream]);

  const chooseSource = useCallback(async (src) => {
    setSource(src);
    if (running) stop();
    await startStream(src, facing);
  }, [facing, running, stop, startStream]);

  const submitQuestion = useCallback((raw) => {
    const q = (raw || "").trim();
    if (!q) return;
    questionRef.current = q;
    setAnalyses((prev) => [{ id: `q-${Date.now()}`, text: q, at: Date.now(), isQuestion: true }, ...prev].slice(0, 25));
    if (!runningRef.current) start();
    else toast.success("Question prise en compte pour la prochaine analyse");
  }, [start]);

  const askQuestion = useCallback(() => {
    submitQuestion(question);
    setQuestion("");
  }, [question, submitQuestion]);

  const toggleVoiceAsk = useCallback(async () => {
    unlockAudio();
    if (whisperRecording) {
      const res = await stopWhisper();
      const text = (res && res.text || "").trim();
      if (text) submitQuestion(text);
      else toast.error("Je n'ai rien entendu, réessaie.");
    } else {
      stopSpeaking();
      await startWhisper();
    }
  }, [whisperRecording, startWhisper, stopWhisper, stopSpeaking, unlockAudio, submitQuestion]);

  // cleanup on unmount
  useEffect(() => () => {
    runningRef.current = false;
    clearTimeout(timerRef.current);
    stopStream();
    stopSpeaking();
  }, [stopStream, stopSpeaking]);

  return (
    <AppShell title="VISION TEMPS RÉEL" subtitle="Sara voit, diagnostique et vous guide à la voix" wide>
      <div data-testid="vision-page" className="grid gap-6 grid-cols-[minmax(0,1fr)] xl:grid-cols-[1fr_minmax(0,320px)]">
        {/* ---- live stage ---- */}
        <div className="space-y-4 min-w-0">
          <div className="relative mx-auto w-full max-w-[min(1024px,120svh)] aspect-video overflow-hidden rounded-2xl border border-[var(--border-hud)] bg-black">
            <video
              ref={videoRef}
              data-testid="vision-video"
              autoPlay
              playsInline
              muted
              onLoadedMetadata={(e) => e.currentTarget.play().catch(() => {})}
              className="absolute inset-0 h-full w-full bg-black object-cover"
              style={{ touchAction: "manipulation" }}
            />
            <canvas ref={canvasRef} className="hidden" />

            <DetectionOverlay detections={detections} mode={mode} />

            {/* total calories badge (food mode) */}
            {mode === "food" && detections.mode === "food" && typeof detections.total === "number" && (
              <div
                data-testid="vision-total-calories"
                className="absolute left-1/2 top-3 z-20 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-amber-400/60 bg-black/70 px-3 py-1 font-mono-hud text-[11px] font-bold uppercase tracking-wider text-amber-300 backdrop-blur"
              >
                <Flame className="h-3.5 w-3.5" /> {detections.total} kcal
              </div>
            )}


            {/* live badge */}
            {running && (
              <div className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-rose-600/90 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-wider text-white">
                <Radio className="h-3 w-3 animate-pulse" /> Live · {mode}
              </div>
            )}
            {busy && (
              <div className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 font-mono-hud text-[10px] uppercase tracking-wider text-cyan-300">
                <Loader2 className="h-3 w-3 animate-spin" /> Analyse
              </div>
            )}
            {!streamOn && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center text-[#6b7a8d]">
                <Camera className="h-10 w-10 opacity-50" />
                <p className="max-w-xs font-mono-hud text-[11px] leading-relaxed tracking-wide">
                  Choisissez une source puis lancez l'analyse. Sara décrira ce qu'elle voit à voix haute.
                </p>
              </div>
            )}

            {/* scrolling subtitle ticker — the text Sara is speaking */}
            {latest && (
              <div
                data-testid="vision-latest"
                className="absolute inset-x-0 bottom-0 overflow-hidden border-t border-[var(--accent)]/40 bg-gradient-to-t from-black/90 to-black/30 py-2.5"
              >
                <div className="vision-subtitle-track" key={latest}>
                  <span className="px-6 text-sm font-medium tracking-wide text-white drop-shadow">{latest}</span>
                  <span className="px-6 text-sm font-medium tracking-wide text-white drop-shadow" aria-hidden="true">{latest}</span>
                </div>
              </div>
            )}
          </div>

          {/* controls */}
          <div className="flex flex-wrap items-center gap-2">
            {!running ? (
              <button
                data-testid="vision-start-button"
                onClick={start}
                className="hud-btn clip-corner flex items-center gap-2 px-5 py-2.5 text-sm font-bold"
              >
                <Play className="h-4 w-4" /> Lancer l'analyse
              </button>
            ) : (
              <button
                data-testid="vision-stop-button"
                onClick={stop}
                className="clip-corner flex items-center gap-2 border border-[#ff6b6b] bg-[#ff6b6b]/10 px-5 py-2.5 text-sm font-bold text-[#ffb3b3] transition-colors hover:bg-[#ff6b6b]/20"
              >
                <Square className="h-4 w-4" /> Arrêter
              </button>
            )}

            <button
              data-testid="vision-speak-toggle"
              onClick={() => setSpeakOn((s) => !s)}
              title={speakOn ? "Voix activée" : "Voix coupée"}
              className={`clip-corner flex items-center gap-2 border px-3.5 py-2.5 text-sm transition-colors ${
                speakOn
                  ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                  : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"
              }`}
            >
              {speakOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              {speakOn ? "Voix" : "Muet"}
            </button>

            {source === "camera" && (
              <button
                data-testid="vision-flip-camera"
                onClick={flipCamera}
                className="clip-corner flex items-center gap-2 border border-[var(--border-hud)] px-3.5 py-2.5 text-sm text-[#8b949e] transition-colors hover:text-white"
              >
                <SwitchCamera className="h-4 w-4" /> Retourner
              </button>
            )}
          </div>

          {/* question box */}
          <div className="flex items-center gap-2">
            <button
              data-testid="vision-mic-button"
              onClick={toggleVoiceAsk}
              title={whisperRecording ? "Arrêter et envoyer" : "Parler à Sara"}
              className={`clip-corner flex items-center gap-2 border px-3.5 py-2.5 text-sm transition-colors ${
                whisperRecording
                  ? "border-[#ff6b6b] bg-[#ff6b6b]/15 text-[#ffb3b3] animate-pulse"
                  : "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
              }`}
            >
              {whisperRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              {whisperRecording ? "Stop" : "Parler"}
            </button>
            <input
              data-testid="vision-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && askQuestion()}
              placeholder="Parlez ou écrivez une question à Sara…"
              className="flex-1 min-w-0 clip-corner border border-[var(--border-hud)] bg-[rgba(2,8,18,0.8)] px-3 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-[#3a4a5a] focus:border-[var(--accent)] focus:shadow-[var(--accent-glow)]"
            />
            <button
              data-testid="vision-ask-button"
              onClick={askQuestion}
              disabled={!question.trim()}
              className="hud-btn clip-corner flex items-center gap-2 px-4 py-2.5 text-sm font-bold disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ---- side panel ---- */}
        <div className="space-y-4 min-w-0">
          <div className="hud-panel clip-corner p-4">
            <div className="mb-2 font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">SOURCE</div>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                data-testid="vision-source-camera"
                onClick={() => chooseSource("camera")}
                className={`flex items-center justify-center gap-2 clip-corner border px-3 py-2.5 text-sm transition-colors ${
                  source === "camera"
                    ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                    : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"
                }`}
              >
                <Camera className="h-4 w-4" /> Caméra
              </button>
              <button
                data-testid="vision-source-screen"
                onClick={() => chooseSource("screen")}
                className={`flex items-center justify-center gap-2 clip-corner border px-3 py-2.5 text-sm transition-colors ${
                  source === "screen"
                    ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                    : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"
                }`}
              >
                <Monitor className="h-4 w-4" /> Écran
              </button>
            </div>
          </div>

          <div className="hud-panel clip-corner p-4">
            <div className="mb-2 font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">MODE</div>
            <div className="space-y-1.5">
              {MODES.map((m) => (
                <button
                  key={m.id}
                  data-testid={`vision-mode-${m.id}`}
                  onClick={() => setMode(m.id)}
                  className={`flex w-full items-center gap-3 clip-corner border px-3 py-2.5 text-left transition-colors ${
                    mode === m.id
                      ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                      : "border-[var(--border-hud)] text-[#8b949e] hover:text-white"
                  }`}
                >
                  <m.icon className="h-4 w-4 shrink-0" />
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold">{m.label}</span>
                    <span className="block truncate text-[11px] text-[#6b7a8d]">{m.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="hud-panel clip-corner p-4">
            <div className="mb-2 font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e]">JOURNAL</div>
            <div data-testid="vision-log" className="max-h-[280px] space-y-2 overflow-y-auto">
              {analyses.length === 0 && (
                <p className="font-mono-hud text-[11px] leading-relaxed text-[#556]">
                  » Les observations de Sara apparaîtront ici.
                </p>
              )}
              {analyses.map((a) => (
                <div
                  key={a.id}
                  className={`clip-corner border-l-2 px-3 py-2 text-[13px] leading-snug ${
                    a.isQuestion
                      ? "border-[#8b949e] bg-[rgba(255,255,255,0.03)] text-[#c8d6e5] italic"
                      : "border-[var(--accent)] bg-[var(--accent-a10)] text-[#eaf4ff]"
                  }`}
                >
                  {a.isQuestion ? `Vous : ${a.text}` : a.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
